'use client'

/**
 * Language Selector Component
 * Allows users to switch between supported languages (German, Arabic, English)
 * Stores preference in cookie and reloads page to apply new locale
 * Matches navbar paper design system
 */

import { useTransition } from 'react'
import { useTranslation } from 'react-i18next'
import { locales, localeNames, localeFlags, type Locale } from '@/i18n/config'
import { persistLocale } from '@/lib/locale-persistence'
import { hardReload } from '@/lib/hard-nav'
import { Globe, Check } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu'

export function LanguageSelector() {
  const { i18n } = useTranslation()
  const currentLocale = i18n.language as Locale
  const [isPending, startTransition] = useTransition()

  function handleLocaleChange(newLocale: Locale) {
    if (newLocale === currentLocale) return

    // Guard against SSR - only run in browser
    if (typeof window === 'undefined') return

    startTransition(() => {
      // Persist to localStorage (+ cookie). localStorage is what survives the
      // hard reload on iOS's capacitor://localhost scheme, where cookies don't —
      // without it the boot-time detector falls back to the device language.
      persistLocale(newLocale)

      // Reload page to apply new locale. hardReload releases PowerSync's native
      // connection first so the reload doesn't collide on it (code 5) on native.
      void hardReload()
    })
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          disabled={isPending}
          className="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md border border-border bg-card hover:bg-accent hover:border-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Select language"
        >
          <Globe className="w-4 h-4" />
          <span className="text-base">{localeFlags[currentLocale]}</span>
          <span className="hidden sm:inline">{localeNames[currentLocale]}</span>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="paper-dropdown-menu min-w-[180px]">
        {locales.map((locale) => (
          <DropdownMenuItem
            key={locale}
            onClick={() => handleLocaleChange(locale)}
            disabled={isPending || locale === currentLocale}
            className={`paper-dropdown-item cursor-pointer ${
              locale === currentLocale ? 'bg-accent' : ''
            }`}
          >
            <span className="text-lg mr-2">{localeFlags[locale]}</span>
            <span className="flex-1">{localeNames[locale]}</span>
            {locale === currentLocale && (
              <Check className="w-4 h-4 text-primary" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
